import { isInitializeRequest } from '@modelcontextprotocol/sdk/types.js';
import { NextFunction, RequestHandler, Response } from 'express';

import { getConfig } from '../../config.js';
import { log } from '../../logging/logger.js';
import { getToolNameFromRequestBody } from '../requestUtils.js';
import { AccessTokenValidator } from './accessTokenValidator.js';
import {
  formatScopes,
  getRequiredApiScopesForTool,
  getRequiredScopesForTool,
  getSupportedApiScopes,
  getSupportedMcpScopes,
} from './scopes.js';
import { AuthenticatedRequest } from './types.js';

const protectedResourceMetadataPath = '/.well-known/oauth-protected-resource';

/**
 * Express middleware for OAuth authentication
 *
 * This middleware checks for Bearer token authorization.
 * If no token is present, returns 401 with WWW-Authenticate header
 * pointing to resource metadata endpoint.
 *
 * @returns Express middleware function
 */
export function authMiddleware(accessTokenValidator: AccessTokenValidator): RequestHandler {
  return async (req: AuthenticatedRequest, res: Response, next: NextFunction): Promise<void> => {
    if (req.auth) {
      // Auth already defined by previous middleware
      next();
      return;
    }

    const authHeader = req.headers.authorization;

    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      // For SSE requests (GET), provide proper SSE error response
      if (req.method === 'GET' && req.headers.accept?.includes('text/event-stream')) {
        res.writeHead(401, {
          'Content-Type': 'text/event-stream',
          'Cache-Control': 'no-cache',
          Connection: 'keep-alive',
        });
        res.write('event: error\n');
        res.write(
          'data: {"error": "unauthorized", "error_description": "Authorization required"}\n\n',
        );
        res.end();
        return;
      }

      const { enforceScopes, advertiseApiScopes, resourceUri } = getConfig().oauth;
      const baseUrl = new URL(resourceUri).origin;
      const resourceMetadataUrl = `${baseUrl}${protectedResourceMetadataPath}`;
      const requiredMcpScopes = await getRequiredMcpScopesForRequest(req.body);
      const requiredApiScopes = await getRequiredApiScopesForRequest(req.body, advertiseApiScopes);
      const scopeParam =
        enforceScopes && requiredMcpScopes.length > 0
          ? `, scope="${formatScopes([...requiredMcpScopes, ...requiredApiScopes])}"`
          : '';
      res
        .status(401)
        .header(
          'WWW-Authenticate',
          `Bearer realm="MCP", resource_metadata="${resourceMetadataUrl}"${scopeParam}`,
        )
        .json({
          error: 'unauthorized',
          error_description: 'Authorization required. Use OAuth 2.1 flow.',
        });
      return;
    }

    const token = authHeader.slice(7);
    const result = await accessTokenValidator.validate(token);

    if (result.isErr()) {
      // For SSE requests (GET), provide proper SSE error response
      if (req.method === 'GET' && req.headers.accept?.includes('text/event-stream')) {
        res.writeHead(401, {
          'Content-Type': 'text/event-stream',
          'Cache-Control': 'no-cache',
          Connection: 'keep-alive',
        });
        res.write('event: error\n');
        res.write(`data: {"error": "invalid_token", "error_description": "${result.error}"}\n\n`);
        res.end();
        return;
      }

      log({
        message: `Access token validation failed: ${result.error}`,
        level: 'error',
        logger: 'oauth',
        data: result.error,
      });
      res.status(401).json({
        error: 'invalid_token',
        error_description: result.error,
      });
      return;
    }
    const authInfo = result.value;
    const { enforceScopes, advertiseApiScopes } = getConfig().oauth;
    if (enforceScopes) {
      const requiredMcpScopes = await getRequiredMcpScopesForRequest(req.body, authInfo.clientId);
      const requiredApiScopes = await getRequiredApiScopesForRequest(
        req.body,
        advertiseApiScopes,
        authInfo.clientId,
      );
      const missingMcpScopes = requiredMcpScopes.filter(
        (scope) => !authInfo.scopes.includes(scope),
      );
      const shouldCheckApiScopes = advertiseApiScopes;
      const missingApiScopes = shouldCheckApiScopes
        ? requiredApiScopes.filter((scope) => !authInfo.scopes.includes(scope))
        : [];
      const missingScopes = [...missingMcpScopes, ...missingApiScopes];

      if (missingScopes.length > 0) {
        log({
          message: `Insufficient scopes: missing [${missingScopes.join(', ')}]`,
          level: 'error',
          logger: 'oauth',
        });
        const { resourceUri } = getConfig().oauth;
        const baseUrl = new URL(resourceUri).origin;
        const resourceMetadataUrl = `${baseUrl}${protectedResourceMetadataPath}`;
        const requiredScopesForChallenge = [
          ...requiredMcpScopes,
          ...(shouldCheckApiScopes ? requiredApiScopes : []),
        ];
        const scopeParam = `scope="${formatScopes(requiredScopesForChallenge)}"`;
        const wwwAuthenticate = `Bearer realm="MCP", error="insufficient_scope", error_description="Missing required scopes", resource_metadata="${resourceMetadataUrl}", ${scopeParam}`;

        if (req.method === 'GET' && req.headers.accept?.includes('text/event-stream')) {
          res.writeHead(403, {
            'Content-Type': 'text/event-stream',
            'Cache-Control': 'no-cache',
            Connection: 'keep-alive',
            'WWW-Authenticate': wwwAuthenticate,
          });
          res.write('event: error\n');
          res.write(
            'data: {"error": "insufficient_scope", "error_description": "Missing required scopes"}\n\n',
          );
          res.end();
          return;
        }

        res.status(403).header('WWW-Authenticate', wwwAuthenticate).json({
          error: 'insufficient_scope',
          error_description: 'Missing required scopes',
        });
        return;
      }
    }

    req.auth = authInfo;
    next();
  };
}

async function getRequiredMcpScopesForRequest(body: unknown, clientId?: string): Promise<string[]> {
  if (isInitializeRequest(body)) {
    return getSupportedMcpScopes(clientId);
  }

  const toolName = getToolNameFromRequestBody(body);
  if (toolName === undefined) {
    return getSupportedMcpScopes(clientId);
  }

  const scopes = new Set<string>();
  for (const scope of getRequiredScopesForTool(toolName)) {
    scopes.add(scope);
  }

  return Array.from(scopes);
}

async function getRequiredApiScopesForRequest(
  body: unknown,
  includeApiScopes: boolean,
  clientId?: string,
): Promise<string[]> {
  if (!includeApiScopes) {
    return [];
  }

  if (isInitializeRequest(body)) {
    return getSupportedApiScopes(clientId);
  }

  const toolName = getToolNameFromRequestBody(body);
  if (toolName === undefined) {
    return [];
  }

  const scopes = new Set<string>();
  for (const scope of getRequiredApiScopesForTool(toolName)) {
    scopes.add(scope);
  }

  return Array.from(scopes);
}
